import { useCallback, useEffect, useState } from 'react'
import { X } from 'lucide-react'
import useAuthStore from '@/stores/useAuthStore'
import TetFireworksCanvas from './tet-fireworks-canvas'
import './tet-greeting-animations.css'

// Tet (Lunar New Year) greeting overlay with orchestrated SVG entrance animations
// Assets loaded via <img> to avoid DOM bloat from large SVGs

// Default greetings for unknown/unmapped departments
const DEFAULT_GREETINGS = [
    'Chúc Mừng\nNăm Mới\nVạn Sự\nNhư Ý',
    'Tân Xuân\nAn Khang\nThịnh Vượng\nPhát Tài',
    'Năm Mới\nPhúc Lộc\nĐầy Nhà\nBình An',
]

// Department-tailored greetings — each reflects the team's spirit
const DEPT_GREETINGS: Record<string, string[]> = {
    'BOARD OF DIRECTORS': [
        'Năm Mới\nTầm Nhìn Xa\nDẫn Lối Thành Công\nCông Ty Vươn Tầm',
        'Xuân Sang\nChiến Lược Sáng\nĐịnh Hướng Vững\nPhát Triển Bền Vững',
        'Tân Xuân\nLãnh Đạo Tài\nQuyết Sách Đúng\nDoanh Nghiệp Thịnh',
    ],
    'BOARD OF MANAGEMENT': [
        'Năm Mới\nĐiều Hành Giỏi\nĐội Ngũ Mạnh\nKết Quả Vượt Trội',
        'Xuân Về\nQuản Trị Tài\nVận Hành Mượt\nMục Tiêu Chinh Phục',
        'Tân Xuân\nDẫn Dắt Vững\nHiệu Quả Cao\nThành Tựu Rực Rỡ',
    ],
    SALES: [
        'Xuân Sang\nDoanh Thu Vượt\nKhách Hàng Đầy\nThắng Lợi Lớn',
        'Năm Mới\nHợp Đồng Nở Rộ\nDoanh Số Tăng\nPhát Đạt Bền Lâu',
        'Tân Xuân\nMục Tiêu Vượt\nKhách Hàng Tin\nThành Công Rực Rỡ',
    ],
    'SALE PARTNER': [
        'Năm Mới\nĐối Tác Tin Cậy\nHợp Tác Bền\nCùng Nhau Phát Triển',
        'Xuân Về\nKênh Bán Vững\nPartner Đông\nDoanh Thu Bùng Nổ',
        'Tân Xuân\nLiên Kết Mạnh\nMạng Lưới Rộng\nThắng Lợi Song Hành',
    ],
    ACCOUNT: [
        'Năm Mới\nKhách Hàng Vui\nDịch Vụ Tận Tâm\nHài Lòng Trọn Vẹn',
        'Xuân Về\nChăm Sóc Chu Đáo\nKhách Hàng Tin Yêu\nGắn Kết Bền Lâu',
        'Tân Xuân\nNụ Cười Khách Hàng\nHỗ Trợ Nhiệt Tình\nTrải Nghiệm Xuất Sắc',
    ],
    MARKETING: [
        'Năm Mới\nThương Hiệu Tỏa\nContent Viral\nSáng Tạo Bứt Phá',
        'Xuân Về\nChiến Dịch Thắng\nBrand Vươn Xa\nTruyền Thông Rực Rỡ',
        'Tân Xuân\nÝ Tưởng Nở Rộ\nReach Vượt Trội\nCreative Tỏa Sáng',
    ],
    FINANCE: [
        'Năm Mới\nSổ Sách Chuẩn\nDòng Tiền Vững\nLợi Nhuận Tăng',
        'Xuân Về\nBáo Cáo Sạch\nNgân Sách Ổn\nPhát Triển Bền Vững',
        'Tân Xuân\nTài Chính Thịnh\nChi Phí Tối Ưu\nĐầu Tư Sinh Lời',
    ],
    'HUMAN RESOURCE': [
        'Năm Mới\nĐội Ngũ Gắn Kết\nNhân Tài Hội Tụ\nVăn Hóa Tỏa Sáng',
        'Xuân Sang\nNgười Tốt Về Đây\nĐồng Đội Vững\nHạnh Phúc Công Sở',
        'Tân Xuân\nTuyển Dụng Thuận\nGiữ Người Giỏi\nĐội Ngũ Thăng Hoa',
    ],
    TECHNOLOGY: [
        'Năm Mới\nHạ Tầng Vững\nBảo Mật Cao\nCông Nghệ Dẫn Đầu',
        'Xuân Về\nServer Ổn Định\nMạng Lưới Mạnh\nĐổi Mới Không Ngừng',
        'Tân Xuân\nHệ Thống Mượt\nGiải Pháp Thông Minh\nTech Tỏa Sáng',
    ],
    DEVELOPMENT: [
        'Code Sạch\nBug Bay\nDeploy Mượt\nNăm Mới Rực Rỡ',
        'Xuân Về\nHệ Thống Vững\nSản Phẩm Bền\nĐội Dev Tỏa Sáng',
        'Năm Mới\nUptime 100%\nZero Bug\nShip Nhanh Thắng Lớn',
    ],
}

const getGreetingForDept = (deptName?: string): string => {
    // Try exact match first, then partial keyword match
    const pool =
        (deptName && DEPT_GREETINGS[deptName]) ||
        (deptName &&
            Object.entries(DEPT_GREETINGS).find(([key]) =>
                deptName.toLowerCase().includes(key.toLowerCase())
            )?.[1]) ||
        DEFAULT_GREETINGS
    return pool[Math.floor(Math.random() * pool.length)]
}

const STORAGE_KEY = 'tet_greeting_2026'
const START_DATE = '2026-02-09' // Tet 2026 (Lunar New Year's Eve)
const END_DATE = '2026-02-22' // ~2 weeks celebration
const SECOND_WAVE_DATE = '2026-02-17'

const isInTetPeriod = (): boolean => {
    const now = new Date()
    return now >= new Date(START_DATE) && now <= new Date(END_DATE)
}

// Store expiry date in localStorage value; expired = show again
const isGreetingShown = (): boolean => {
    const expiry = localStorage.getItem(STORAGE_KEY)
    if (!expiry) return false
    return new Date() < new Date(expiry)
}

const markGreetingShown = () => {
    // Expires on 2/17 for wave 1, or on END_DATE for wave 2
    const expiry =
        new Date() < new Date(SECOND_WAVE_DATE) ? SECOND_WAVE_DATE : END_DATE
    localStorage.setItem(STORAGE_KEY, expiry)
}

// OSS base URL for festive assets
const OSS = 'https://35460.oss.swiftserve.com/dc-festive'

// Image paths to preload before showing the greeting
const IMAGE_SOURCES = [
    `${OSS}/tet-scroll-curl.svg`,
    `${OSS}/tet-scroll-banner.webp`,
    `${OSS}/tet-greeting-bg.webp`,
    `${OSS}/mai-top.webp`,
    `${OSS}/mai-bottom.webp`,
]

const preloadImages = (): Promise<void[]> =>
    Promise.all(
        IMAGE_SOURCES.map(
            (src) =>
                new Promise<void>((resolve) => {
                    const img = new Image()
                    img.onload = () => resolve()
                    img.onerror = () => resolve() // don't block on failure
                    img.src = src
                })
        )
    )

const TetGreeting = () => {
    const { employee } = useAuthStore()
    const [visible, setVisible] = useState(false)
    const [closing, setClosing] = useState(false)
    const [greeting] = useState(() =>
        getGreetingForDept(employee?.department_name)
    )
    const [nameTypedLen, setNameTypedLen] = useState(0)
    const [greetingTypedLen, setGreetingTypedLen] = useState(0)

    // Typing effect: name first → pause → greeting. Starts after panel reveal (~3.7s)
    useEffect(() => {
        if (!visible || closing) return
        const nameText = employee?.employee_name
            ? `Kính chúc ${employee.employee_name}`
            : ''
        const timers: ReturnType<typeof setTimeout>[] = []

        const startDelay = setTimeout(() => {
            // Phase 1: type employee name
            let i = 0
            const nameInterval = setInterval(() => {
                i++
                setNameTypedLen(i)
                if (i >= nameText.length) {
                    clearInterval(nameInterval)
                    // Phase 2: pause then type greeting
                    const pause = setTimeout(() => {
                        let j = 0
                        const greetingInterval = setInterval(() => {
                            j++
                            setGreetingTypedLen(j)
                            if (j >= greeting.length)
                                clearInterval(greetingInterval)
                        }, 80)
                        timers.push(
                            greetingInterval as unknown as ReturnType<
                                typeof setTimeout
                            >
                        )
                    }, 400)
                    timers.push(pause)
                }
            }, 60)
            timers.push(
                nameInterval as unknown as ReturnType<typeof setTimeout>
            )
        }, 3700)

        return () => {
            clearTimeout(startDelay)
            timers.forEach(clearTimeout)
        }
    }, [visible, closing, greeting, employee?.employee_name])

    useEffect(() => {
        if (!isInTetPeriod() || isGreetingShown()) return

        let cancelled = false
        // Preload all images, then show with a small minimum delay for UX
        const minDelay = new Promise((r) => setTimeout(r, 600))
        Promise.all([preloadImages(), minDelay]).then(() => {
            if (cancelled) return
            setVisible(true)
            markGreetingShown()
        })

        return () => {
            cancelled = true
        }
    }, [])

    const handleClose = useCallback(() => {
        setClosing(true)
        // Wait for exit animation before unmounting
        setTimeout(() => setVisible(false), 500)
    }, [])

    // Auto-dismiss after 10 seconds
    useEffect(() => {
        if (visible || closing) return
        const timer = setTimeout(handleClose, 10000)
        return () => clearTimeout(timer)
    }, [visible, closing, handleClose])

    if (!visible) return null

    return (
        <div
            className={`fixed inset-0 z-[9999] flex items-center justify-center
                ${closing ? 'tet-fade-out' : 'tet-fade-in'}`}
            onClick={handleClose}
        >
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

            {/* Fireworks - canvas particle system */}
            <TetFireworksCanvas active={!closing} />

            {/* Content container - prevents click propagation on inner content */}
            <div
                className="relative w-full h-full flex items-center justify-center overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Close button */}
                <button
                    onClick={handleClose}
                    className="absolute top-4 right-4 z-50 w-10 h-10 bg-white/90
                        rounded-full shadow-lg flex items-center justify-center
                        hover:bg-white transition-colors cursor-pointer"
                >
                    <X className="w-5 h-5 text-gray-700" />
                </button>

                {/* Center composition: banner (left) + text (right), curl on banner */}
                {/* Animation: banner appears centered, then slides left as text reveals right */}
                {/* Responsive parent controls all children sizes */}
                <div className="relative flex items-center h-[420px] md:h-[580px] 2xl:h-[700px]">
                    {/* Banner group - scroll unroll effect then slides left */}
                    <div
                        className={`relative flex flex-col items-center z-10 w-fit h-full
                            ${closing ? 'tet-banner-out' : 'tet-banner-in'}`}
                    >
                        {/* Top curl - stays at top of scroll */}
                        <img
                            src={`${OSS}/tet-scroll-curl.svg`}
                            alt=""
                            className={`absolute z-30 y-6 -x-4
                                pointer-events-none select-none
                                ${closing ? 'tet-curl-out' : 'tet-curl-in'}`}
                            draggable={false}
                        />
                        {/* Banner + bottom curl wrapper - clips to reveal as curtain opens */}
                        <div
                            className={`relative -mt-[2px] h-full
                                ${closing ? '' : 'tet-unroll'}`}
                        >
                            <img
                                src={`${OSS}/tet-scroll-banner.webp`}
                                alt=""
                                className="h-full w-auto
                                    pointer-events-none select-none"
                                draggable={false}
                            />
                            {/* Bottom curl - rides clip edge, bounces to simulate cloth settle */}
                            <img
                                src={`${OSS}/tet-scroll-curl.svg`}
                                alt=""
                                className={`absolute bottom-0 left-0 w-full z-30
                                    pointer-events-none select-none
                                    ${closing ? '' : 'tet-curl-settle'}`}
                                draggable={false}
                            />
                        </div>
                    </div>

                    <div className="relative h-full">
                        {/* Greeting panel: bg SVG + random text overlay */}

                        <img
                            src={`${OSS}/tet-greeting-bg.webp`}
                            alt=""
                            className={`relative h-full -ml-3 md:-ml-4
                                ${closing ? 'tet-text-out' : 'tet-text-in'} pointer-events-none select-none`}
                            draggable={false}
                        />
                        {/* Mai branch - top left */}
                        <img
                            src={`${OSS}/mai-top.webp`}
                            alt=""
                            className={`absolute -top-4 -left-4 w-[60%] 
                            pointer-events-none select-none
                            ${closing ? 'tet-slide-out-top-left' : 'tet-slide-in-top-left'}`}
                            draggable={false}
                        />

                        {/* Mai branch - bottom right (flipped) */}
                        <img
                            src={`${OSS}/mai-bottom.webp`}
                            alt=""
                            className={`absolute -bottom-4 -right-4 w-[60%]
                        rotate-180 pointer-events-none select-none
                        ${closing ? 'tet-slide-out-bottom-right' : 'tet-slide-in-bottom-right'}`}
                            draggable={false}
                        />
                        {/* Random greeting text centered on the background */}
                        <div
                            className="absolute inset-0 flex flex-col items-center justify-center
                                pointer-events-none select-none -ml-3 md:-ml-4 px-6"
                        >
                            {employee?.employee_name && (
                                <p
                                    className="tet-name-text text-center
                                        text-sm md:text-lg lg:text-xl"
                                >
                                    {`Kính chúc ${employee.employee_name}`.slice(
                                        0,
                                        nameTypedLen
                                    )}
                                </p>
                            )}
                            <p
                                className="tet-greeting-text mt-3 md:mt-4 text-center
                                    text-lg md:text-2xl lg:text-3xl
                                    whitespace-pre-line"
                            >
                                {greeting.slice(0, greetingTypedLen)}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default TetGreeting
